import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUpload, FiFile, FiFolder, FiSearch, FiFilter, FiTrash2,
  FiEdit2, FiDownload, FiEye, FiPlus, FiDatabase, FiBook, FiMic,
  FiFileText, FiImage, FiVideo, FiMusic, FiCode, FiArchive
} from 'react-icons/fi';
import { BiBot, BiBrain } from 'react-icons/bi';
import { BsRobot } from 'react-icons/bs';
import toast from 'react-hot-toast';
import api, { api as apiInstance } from '../utils/api';
import { useSocket } from '../contexts/SocketContext';
import FileUpload from '../components/FileUpload';
import { Link } from 'react-router-dom';
// import { v4 as uuidv4 } from 'uuid';

interface Document {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadDate: Date;
  status: 'processing' | 'ready' | 'error';
  category: string;
  tags: string[];
  url?: string;
}

interface KnowledgeFolder {
  id: string;
  name: string;
  documents: number;
  created: Date;
  color: string;
}

type AssistantLite = { id: string; name: string; voiceEnabled?: boolean };
type ActiveTag = { id: string; name: string; tag: string; linkedAssistantId?: string | null; updatedAt: string; createdAt: string; linkedAssistant?: AssistantLite | null };
type ScrapedSource = { id: string; url: string; title?: string; vectorsCount?: number; status?: string; createdAt?: string };

const KnowledgeBase: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [folders, setFolders] = useState<KnowledgeFolder[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<any[]>([]);
  const [activeKbId, setActiveKbId] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { socket } = useSocket();
  const [assistants, setAssistants] = useState<AssistantLite[]>([]);
  const [tags, setTags] = useState<ActiveTag[]>([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showScrapeModal, setShowScrapeModal] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [scrapedPreview, setScrapedPreview] = useState<any | null>(null);
  const [scrapedId, setScrapedId] = useState<string | null>(null);
  const [showScrapedContent, setShowScrapedContent] = useState(true);
  const [sources, setSources] = useState<ScrapedSource[]>([]);

  useEffect(() => {
    const loadKbs = async () => {
      try {
        const rsp = await apiInstance.get('/knowledge/knowledge-bases');
        const list = rsp.data?.knowledgeBases || [];
        setKnowledgeBases(list);
        if (list.length) setActiveKbId(list[0].id);
      } catch (e) {
        console.error('Failed to load knowledge bases', e);
      }
    };
    const loadTags = async () => {
      try {
        const [t, a] = await Promise.all([
          apiInstance.get('/knowledge/tags'),
          apiInstance.get('/assistants')
        ]);
        setTags(t.data?.tags || []);
        setAssistants((a.data?.assistants || []).map((x: any) => ({ id: x.id, name: x.name, voiceEnabled: x.voiceEnabled })));
      } catch (e) {
        console.error('Failed to load tags', e);
      }
    };
    loadKbs();
    loadTags();
  }, []);

  useEffect(() => {
    const loadDocs = async () => {
      if (!activeKbId) return;
      try {
        const [docsRsp, srcRsp] = await Promise.all([
          apiInstance.get('/knowledge/documents', { params: { knowledgeBaseId: activeKbId, limit: 50 } }),
          apiInstance.get('/knowledge/sources', { params: { knowledgeBaseId: activeKbId } })
        ]);
        const docs = docsRsp.data?.documents || [];
        const mappedDocs = docs.map((d: any) => ({
          id: d.id,
          name: d.fileName || d.title || 'Document',
          type: d.type || 'txt',
          size: Number(d.fileSize || 0),
          uploadDate: new Date(d.createdAt || Date.now()),
          status: (d.status || 'READY').toString().toLowerCase() as any,
          category: 'Uncategorized',
          tags: [],
          url: undefined,
        }));
        setDocuments(mappedDocs);
        const sourcesData = (srcRsp.data?.sources || []).map((s: any) => ({ id: s.id, url: s.url, title: s.title, vectorsCount: s.vectorsCount || 0, status: s.status, createdAt: s.createdAt }));
        setSources(sourcesData);
      } catch (err) {
        toast.error('Failed to load documents or sources');
        console.error(err);
      }
    };
    loadDocs();
  }, [activeKbId]);

  useEffect(() => {
    if (!socket) return;
    const handler = (evt: any) => {
      if (evt?.type && evt.type.startsWith('document.')) {
        if (activeKbId) {
          apiInstance.get('/knowledge/documents', { params: { knowledgeBaseId: activeKbId, limit: 50 } })
            .then(r => {
              const docs = r.data?.documents || [];
              setDocuments(docs.map((d: any) => ({
                id: d.id,
                name: d.fileName || d.title || 'Document',
                type: d.type || 'txt',
                size: Number(d.fileSize || 0),
                uploadDate: new Date(d.createdAt || Date.now()),
                status: (d.status || 'READY').toString().toLowerCase() as any,
                category: 'Uncategorized',
                tags: [],
                url: undefined,
              })));
            })
            .catch(() => {});
        }
      }
    };
    socket.on('knowledgeBase.updated', handler);
    socket.on('document.processed', handler);
    return () => { socket.off('knowledgeBase.updated', handler); };
  }, [socket, activeKbId]);

  const createKb = useCallback(async () => {
    try {
      const name = `Knowledge Base ${new Date().toLocaleString()}`;
      const rsp = await apiInstance.post('/knowledge/knowledge-bases', { name });
      const kb = rsp.data?.knowledgeBase;
      setKnowledgeBases(prev => [kb, ...prev]);
      setActiveKbId(kb.id);
      toast.success('Knowledge base created');
    } catch (e) {
      toast.error('Failed to create knowledge base');
    }
  }, []);

  const toggleKbVoice = useCallback(async (kb: any) => {
    try {
      const rsp = await apiInstance.patch(`/knowledge/knowledge-bases/${kb.id}`, { voiceEnabled: !kb.voiceEnabled });
      const updated = rsp.data?.knowledgeBase;
      setKnowledgeBases(prev => prev.map(k => k.id === kb.id ? updated : k));
      toast.success(`Voice ${updated.voiceEnabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error('Failed to toggle voice');
    }
  }, []);

  const getFileIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pdf': return <FiFileText className="text-red-500" size={20} />;
      case 'docx':
      case 'doc': return <FiFile className="text-blue-500" size={20} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <FiImage className="text-green-500" size={20} />;
      case 'mp4':
      case 'avi':
      case 'mov': return <FiVideo className="text-purple-500" size={20} />;
      case 'mp3':
      case 'wav': return <FiMusic className="text-pink-500" size={20} />;
      case 'js':
      case 'ts':
      case 'py':
      case 'java': return <FiCode className="text-yellow-500" size={20} />;
      case 'zip':
      case 'rar': return <FiArchive className="text-orange-500" size={20} />;
      default: return <FiFile className="text-gray-500" size={20} />;
    }
  };

  // Extract file type from URL to prevent duplication
  const getFileTypeFromUrl = (url: string): string => {
    try {
      const extension = url.split('.').pop()?.toLowerCase() || '';
      return extension || 'url';
    } catch {
      return 'url';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFiles = async (files: File[]) => {
    for (const file of files) {
      const docId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      setUploadProgress(prev => ({ ...prev, [docId]: 0 }));
      const newDoc: Document = {
        id: docId,
        name: file.name,
        type: file.name.split('.').pop() || '',
        size: file.size,
        uploadDate: new Date(),
        status: 'processing',
        category: 'Uncategorized',
        tags: []
      };
      setDocuments(prev => [...prev, newDoc]);
      try {
        if (!activeKbId) {
          toast.error('Create a knowledge base first');
          return;
        }
        // Initiate
        const init = await apiInstance.post('/knowledge/documents/upload', {
          knowledgeBaseId: activeKbId,
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
        });
        const { document } = init.data;
        // Fallback to multipart legacy if no URL returned
        const form = new FormData();
        form.append('file', file);
        form.append('knowledgeBaseId', activeKbId);
        await apiInstance.post('/knowledge/documents', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt: any) => {
            if (evt.total) {
              const percent = Math.round((evt.loaded * 100) / evt.total);
              setUploadProgress(prev => ({ ...prev, [docId]: percent }));
            }
          }
        });
        setDocuments(docs => docs.map(d => d.id === docId ? { ...d, status: 'ready' } : d));
        setUploadProgress(prev => ({ ...prev, [docId]: 100 }));
        toast.success(`${file.name} uploaded successfully`);
      } catch (e) {
        setDocuments(docs => docs.map(d => d.id === docId ? { ...d, status: 'error' } : d));
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterType === 'all' || doc.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const CreateFolderModal = () => (
    <AnimatePresence>
      {showCreateFolder && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowCreateFolder(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 w-full max-w-md rounded-2xl p-6"
            style={{
              background: 'rgba(30, 41, 59, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <h2 className="text-xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Create New Folder
            </h2>
            <input
              type="text"
              placeholder="Folder name"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 mb-4"
            />
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setShowCreateFolder(false);
                  toast.success('Folder created successfully!');
                }}
                className="flex-1 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium"
              >
                Create
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCreateFolder(false)}
                className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const [tagForm, setTagForm] = useState<{ id?: string; name: string; tag: string; linkedAssistantId?: string | null }>({ name: '', tag: '', linkedAssistantId: null });
  const [showTagsTable, setShowTagsTable] = useState(false);
  const [tagsPagination, setTagsPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [loading, setLoading] = useState({ tags: false, sources: false, kb: false });

  // Memoized TagModal to prevent re-renders
  const TagModal = React.memo(() => {
    const [localForm, setLocalForm] = useState({ name: '', tag: '', linkedAssistantId: null as string | null, id: undefined as string | undefined });
    
    // Initialize form when modal opens
    React.useEffect(() => {
      if (showTagModal) {
        setLocalForm({
          name: tagForm.name || '',
          tag: tagForm.tag || '',
          linkedAssistantId: tagForm.linkedAssistantId || null,
          id: tagForm.id
        });
      }
    }, [showTagModal]);

    if (!showTagModal) return null;

    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 z-50 flex items-center justify-center p-4" 
        onClick={() => setShowTagModal(false)}
      >
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} />
        <motion.div 
          initial={{ scale: 0.95, y: 12 }} 
          animate={{ scale: 1, y: 0 }} 
          exit={{ scale: 0.95, y: 12 }} 
          onClick={(e) => e.stopPropagation()} 
          className="relative z-10 w-full max-w-lg rounded-lg p-5" 
          style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
        >
          <h2 className="text-lg font-semibold mb-3">Active tag</h2>
          <div className="space-y-3">
            <label className="block text-sm">Name
              <input 
                value={localForm.name} 
                onChange={(e) => setLocalForm(f => ({ ...f, name: e.target.value }))} 
                className="w-full mt-1 px-3 py-2 rounded border" 
                style={{ background: 'transparent', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }} 
              />
            </label>
            <label className="block text-sm">Linked assistant
              <select 
                value={localForm.linkedAssistantId || ''} 
                onChange={(e) => setLocalForm(f => ({ ...f, linkedAssistantId: e.target.value || null }))} 
                className="w-full mt-1 px-3 py-2 rounded border" 
                style={{ background: 'transparent', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }}
              >
                <option value="">Select assistant</option>
                {assistants.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="block text-sm">Active Tag
              <input 
                value={localForm.tag} 
                onChange={(e) => setLocalForm(f => ({ ...f, tag: e.target.value }))} 
                className="w-full mt-1 px-3 py-2 rounded border" 
                placeholder="cl_booking_assistant" 
                style={{ background: 'transparent', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }} 
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button 
              onClick={() => setShowTagModal(false)} 
              className="px-4 py-2 rounded" 
              style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}
            >
              Cancel
            </button>
            <button 
              onClick={async () => {
                try {
                  if (!localForm.name || !localForm.tag) { 
                    toast.error('Name and Active Tag are required'); 
                    return; 
                  }
                  if (localForm.id) {
                    const rsp = await apiInstance.put(`/knowledge/tags/${localForm.id}`, { 
                      name: localForm.name, 
                      tag: localForm.tag, 
                      linkedAssistantId: localForm.linkedAssistantId || undefined 
                    });
                    setTags(prev => prev.map(t => t.id === localForm.id ? rsp.data.tag : t));
                  } else {
                    const rsp = await apiInstance.post('/knowledge/tags', { 
                      name: localForm.name, 
                      tag: localForm.tag, 
                      linkedAssistantId: localForm.linkedAssistantId || undefined 
                    });
                    setTags(prev => [rsp.data.tag, ...prev]);
                  }
                  setShowTagModal(false);
                  toast.success('Saved');
                } catch (e: any) { 
                  toast.error(e?.response?.data?.message || 'Failed to save'); 
                }
              }} 
              className="px-4 py-2 rounded" 
              style={{ background: 'var(--color-primary)', color: '#fff' }}
            >
              Save changes
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  });

  const TagsOverviewDashboard = () => {
    const startIdx = (tagsPagination.page - 1) * tagsPagination.limit;
    const endIdx = startIdx + tagsPagination.limit;
    const paginatedTags = tags.slice(startIdx, endIdx);
    const totalPages = Math.ceil(tags.length / tagsPagination.limit);

    const deleteTag = async (tagId: string) => {
      if (!confirm('Delete this tag?')) return;
      setLoading(prev => ({ ...prev, tags: true }));
      try {
        await apiInstance.delete(`/knowledge/tags/${tagId}`);
        setTags(prev => prev.filter(t => t.id !== tagId));
        setTagsPagination(prev => ({ ...prev, total: tags.length - 1 }));
        toast.success('Tag deleted');
      } catch (e: any) {
        toast.error(e.response?.data?.error || 'Failed to delete tag');
      } finally {
        setLoading(prev => ({ ...prev, tags: false }));
      }
    };

    const editTag = (tag: ActiveTag) => {
      setTagForm({ id: tag.id, name: tag.name, tag: tag.tag, linkedAssistantId: tag.linkedAssistantId });
      setShowTagModal(true);
    };

    return (
      <div className="space-y-4 mb-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Active Tags Overview</h3>
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setTagForm({ id: undefined, name: '', tag: '', linkedAssistantId: null }); setShowTagModal(true); }}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2"
              style={{ background: 'var(--color-primary)', color: '#fff' }}
            >
              <FiPlus size={16} />
              Create Active Tag
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowTagsTable(!showTagsTable)}
              className="px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-colors"
              style={{ background: showTagsTable ? 'var(--color-bg-tertiary)' : 'var(--color-primary)', color: showTagsTable ? 'var(--color-text-secondary)' : '#fff' }}
            >
              {showTagsTable ? 'Hide' : 'Show'} Tags Table
            </motion.button>
          </div>
        </div>

        {showTagsTable && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="rounded-lg overflow-hidden"
            style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: 'var(--color-bg-tertiary)' }}>
                    <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>NAME</th>
                    <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>UPDATED</th>
                    <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>TAG</th>
                    <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>LINKED ASSISTANT</th>
                    <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>STATUS</th>
                    <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading.tags && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <div className="flex items-center justify-center gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                          Loading...
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading.tags && paginatedTags.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
                        No tags found.
                      </td>
                    </tr>
                  )}
                  {!loading.tags && paginatedTags.map((tag, idx) => (
                    <tr key={tag.id} style={{ borderTop: idx > 0 ? '1px solid var(--color-border-primary)' : 'none' }}>
                      <td className="px-4 py-3" style={{ color: 'var(--color-text-primary)' }}>{tag.name}</td>
                      <td className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {new Date(tag.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded text-xs font-mono" style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)' }}>
                          {tag.tag}
                        </span>
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--color-text-secondary)' }}>
                        {tag.linkedAssistant?.name || 'None'}
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const linked = !!tag.linkedAssistant;
                          const badgeClass = linked
                            ? 'bg-green-600/20 text-green-400'
                            : 'bg-yellow-600/20 text-yellow-400';
                          const label = linked ? 'ACTIVE' : 'UNLINKED';
                          return (
                            <span className={`px-2 py-1 rounded text-[11px] font-semibold tracking-wide uppercase ${badgeClass}`}>{label}</span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => editTag(tag)} className="p-1 rounded hover:bg-gray-100" title="Edit">
                            <FiEdit2 size={14} style={{ color: 'var(--color-text-secondary)' }} />
                          </button>
                          <button onClick={() => deleteTag(tag.id)} className="p-1 rounded hover:bg-red-100" title="Delete">
                            <FiTrash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {tags.length > 0 && (
              <div className="px-4 py-3 flex justify-between items-center" style={{ background: 'var(--color-bg-tertiary)', borderTop: '1px solid var(--color-border-primary)' }}>
                <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {startIdx + 1}-{Math.min(endIdx, tags.length)} of {tags.length} Results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTagsPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={tagsPagination.page === 1}
                    className="px-3 py-1 rounded text-sm disabled:opacity-50"
                    style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm" style={{ color: 'var(--color-text-primary)' }}>
                    {tagsPagination.page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setTagsPagination(prev => ({ ...prev, page: Math.min(totalPages, prev.page + 1) }))}
                    disabled={tagsPagination.page === totalPages}
                    className="px-3 py-1 rounded text-sm disabled:opacity-50"
                    style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    );
  };

  // Memoized WebScrapeModal to prevent re-renders
  const WebScrapeModal = React.memo(() => {
    const [localScrapeUrl, setLocalScrapeUrl] = useState('');
    const [localScrapeLoading, setLocalScrapeLoading] = useState(false);
    const [localScrapedPreview, setLocalScrapedPreview] = useState<any | null>(null);
    const [localScrapedId, setLocalScrapedId] = useState<string | null>(null);
    const [localShowScrapedContent, setLocalShowScrapedContent] = useState(true);

    // Reset form when modal opens
    React.useEffect(() => {
      if (showScrapeModal) {
        setLocalScrapeUrl(scrapeUrl);
        setLocalScrapedPreview(scrapedPreview);
        setLocalScrapedId(scrapedId);
        setLocalShowScrapedContent(showScrapedContent);
      }
    }, [showScrapeModal]);

    if (!showScrapeModal) return null;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowScrapeModal(false)}>
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} />
          <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }} onClick={(e) => e.stopPropagation()} className="relative z-10 w-full max-w-2xl rounded-lg p-5 space-y-3" style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}>
            <h2 className="text-lg font-semibold">Web Scrape</h2>
            <input value={localScrapeUrl} onChange={(e) => setLocalScrapeUrl(e.target.value)} placeholder="vitalitymedicalcentre.com.au" className="w-full px-3 py-2 rounded border" style={{ background: 'transparent', borderColor: 'var(--color-border-primary)', color: 'var(--color-text-primary)' }} />
            <div className="flex items-center gap-2">
              <button disabled={localScrapeLoading || !localScrapeUrl} onClick={async () => {
                try {
                  if (!localScrapeUrl.trim()) {
                    toast.error('Please enter a URL');
                    return;
                  }
                  
                  // Validate and format URL
                  let urlToScrape = localScrapeUrl.trim();
                  if (!urlToScrape.startsWith('http://') && !urlToScrape.startsWith('https://')) {
                    urlToScrape = 'https://' + urlToScrape;
                  }
                  
                  setLocalScrapeLoading(true);
                  console.log('Scraping URL:', urlToScrape);
                  
                  const rsp = await apiInstance.post('/webscraper/scrape', { url: urlToScrape });
                  console.log('Scrape response:', rsp.data);
                  
                  if (rsp.data?.success) {
                    setLocalScrapedPreview(rsp.data.data || null);
                    setLocalScrapedId(rsp.data.id || null);
                    // Update parent state
                    setScrapedPreview(rsp.data.data || null);
                    setScrapedId(rsp.data.id || null);
                    toast.success(`Website scraped successfully! Found: ${rsp.data.data?.title || 'content'}`);
                  } else {
                    toast.error(rsp.data?.error || 'Failed to scrape website');
                    console.error('Scrape error:', rsp.data);
                  }
                } catch (e: any) { 
                  console.error('Scrape request failed:', e);
                  const errorMsg = e?.response?.data?.error || e?.response?.data?.details || e?.message || 'Failed to scrape website';
                  toast.error(errorMsg);
                }
                finally { setLocalScrapeLoading(false); }
              }} className="px-3 py-2 rounded" style={{ background: 'var(--color-primary)', color: '#fff' }}>{localScrapeLoading ? 'Scraping…' : 'Scrape'}</button>
              {localScrapedPreview && (
                <button onClick={() => setLocalShowScrapedContent(v => !v)} className="px-3 py-2 rounded border" style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)' }}>{localShowScrapedContent ? 'Hide Data' : 'Toggle Data'}</button>
              )}
              {localScrapedPreview && activeKbId && (
                <button onClick={async () => {
                  try {
                    if (!localScrapedId) return;
                    await apiInstance.post('/knowledge/sources/link', { knowledgeBaseId: activeKbId, scrapedId: localScrapedId });
                    const ingest = await apiInstance.post('/knowledge/scraped/ingest', { knowledgeBaseId: activeKbId, scrapedId: localScrapedId });
                    toast.success(`Saved ${ingest.data?.vectors || 0} vectors`);
                    const srcRsp = await apiInstance.get('/knowledge/sources', { params: { knowledgeBaseId: activeKbId } });
                    setSources((srcRsp.data?.sources || []).map((s: any) => ({ id: s.id, url: s.url, title: s.title, vectorsCount: s.vectorsCount || 0, status: s.status, createdAt: s.createdAt })));
                  } catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to save'); }
                }} className="px-3 py-2 rounded" style={{ background: 'var(--color-primary)', color: '#fff' }}>Save to Knowledge Base</button>
              )}
            </div>
            {localScrapedPreview && localShowScrapedContent && (
              <div className="max-h-[50vh] overflow-auto rounded border p-3 text-sm" style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)' }}>
                <div className="font-semibold mb-2">{localScrapedPreview.title}</div>
                <div className="mb-2"><span className="opacity-75">URL:</span> {localScrapedPreview.url}</div>
                <pre style={{ whiteSpace: 'pre-wrap' }}>{localScrapedPreview.content}</pre>
              </div>
            )}
          </motion.div>
        </motion.div>
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 15 + i * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          >
            <FiDatabase size={30 + i * 5} className="text-blue-500/20" />
          </motion.div>
        ))}
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 mb-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Knowledge Base
            </h1>
            <p className="text-gray-400 mt-2">Manage knowledge bases, sources and voice</p>
          </div>
          
          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={createKb}
              className="px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2"
              style={{ background: 'var(--color-primary)' }}
            >
              <FiPlus size={18} />
              Create Knowledge Base
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowScrapeModal(true)}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:opacity-90"
              style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}
            >
              New Data
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => { setTagForm({ id: undefined, name: '', tag: '', linkedAssistantId: null }); setShowTagModal(true); }}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:opacity-90"
              style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}
            >
              Create Active Tag
            </motion.button>
          </div>
        </div>

        {/* Knowledge Bases Grid */}
        <div className="mb-6">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-md)' }}>
            {knowledgeBases.map(kb => (
              <div key={kb.id} className="rounded-xl p-4 border" style={{ background: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
                <div className="flex items-start justify-between w-full gap-2 overflow-hidden">
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate">{kb.name}</div>
                    <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{new Date(kb.createdAt || Date.now()).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={() => toggleKbVoice(kb)}
                      className="p-2 rounded"
                      title="Toggle voice"
                      style={{ background: 'transparent', color: kb.voiceEnabled ? 'var(--color-success)' : 'var(--color-text-secondary)' }}
                    >
                      <FiMic size={14} />
                    </button>
                    <button
                      onClick={() => setActiveKbId(kb.id)}
                      className="p-2 rounded"
                      title="Details"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <FiEye size={14} />
                    </button>
                    <button
                      onClick={async () => {
                        await apiInstance.delete(`/knowledge/knowledge-bases/${kb.id}`);
                        setKnowledgeBases(prev => prev.filter(x => x.id !== kb.id));
                        if (activeKbId === kb.id) setActiveKbId(null);
                      }}
                      className="p-2 rounded"
                      title="Delete"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Sources: <span className="font-semibold">0 sources</span>
                </div>
                <div className="mt-2">
                  <Link to="#" className="text-xs underline" style={{ color: 'var(--color-text-secondary)' }}>ENABLE VOICE</Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags Overview Dashboard */}
        <TagsOverviewDashboard />

        {/* Search and Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
            />
          </div>
          
          {/* Dynamically generate file-type filter buttons based on loaded documents */}
          <div className="flex gap-2">
            {React.useMemo(() => {
              const types = Array.from(new Set(documents.map(d => d.type?.toLowerCase?.() || ''))).filter(Boolean);
              return ['all', ...types];
            }, [documents]).map((type) => (
              <motion.button
                key={type}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg capitalize transition-all ${
                  filterType === type
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {type}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Removed folder cards per new design */}
      {/* Removed upload area per design */}
      {/* Removed documents grid per design */}

      {/* Knowledge Base Management System - Sources table for selected KB */}
      {activeKbId && (
        <div className="rounded-lg border mt-8" style={{ borderColor: 'var(--color-border-primary)', background: 'var(--color-bg-secondary)' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Knowledge Base Sources
            </h3>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Manage documents and sources for this knowledge base
            </p>
          </div>
          
          <div className="grid text-xs font-medium" style={{ gridTemplateColumns: 'minmax(240px,1fr) 140px 120px 160px 120px 140px', padding: '12px 16px', color: 'var(--color-text-secondary)', background: 'var(--color-bg-tertiary)' }}>
            <div>SOURCE</div>
            <div>DATA POINTS</div>
            <div>TYPE</div>
            <div>CREATED</div>
            <div>STATUS</div>
            <div>ACTIONS</div>
          </div>
          
          {sources.map((s) => {
            const fileType = getFileTypeFromUrl(s.url);
            const fileIcon = getFileIcon(fileType);
            return (
            <div key={`${s.id}-${s.url}`} className="grid items-center hover:bg-opacity-50" style={{ gridTemplateColumns: 'minmax(240px,1fr) 140px 120px 160px 120px 140px', padding: '12px 16px', borderTop: '1px solid var(--color-border-primary)' }}>
              <div style={{ color: 'var(--color-text-primary)' }} className="truncate">
                <div className="font-medium flex items-center gap-2">
                  {fileIcon}
                  {s.title || 'Untitled'}
                </div>
                <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{s.url}</div>
              </div>
              <div style={{ color: 'var(--color-text-secondary)' }}>
                <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{s.vectorsCount || 0}</span> vectors
              </div>
                <div>
                <span className="px-2 py-1 rounded text-[11px] font-semibold tracking-wide uppercase" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--color-text-secondary)' }}>
                  {fileType.toUpperCase()}
                </span>
                </div>
              <div style={{ color: 'var(--color-text-secondary)' }} className="text-sm">
                {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '-'}
              </div>
              <div>
                {(() => {
                  const status = (s.status || 'READY').toString().toUpperCase();
                  const badgeClass = status === 'READY'
                    ? 'bg-green-600/20 text-green-400'
                    : status === 'ERROR'
                    ? 'bg-red-600/20 text-red-400'
                    : 'bg-yellow-600/20 text-yellow-400';
                  return (
                    <span className={`px-2 py-1 rounded text-[11px] font-semibold tracking-wide uppercase ${badgeClass}`}>{status}</span>
                  );
                })()}
              </div>
              <div className="flex gap-2">
                <button 
                  className="px-3 py-1 rounded text-xs font-medium hover:opacity-80"
                  style={{ background: 'var(--color-primary)', color: '#fff' }}
                  title="Test Results"
                  onClick={async () => {
                    try {
                      const r = await apiInstance.get(`/knowledge/doc/${s.id}`);
                      toast.success(r.data?.name || 'Loaded');
                    } catch {
                      toast.error('Failed to load test data');
                    }
                  }}
                >
                  Test Results
                </button>
                <button 
                  className="p-1 rounded hover:bg-red-100" 
                  title="Delete"
                  onClick={async () => {
                    if (confirm('Delete this source?')) {
                      try {
                        await apiInstance.delete(`/knowledge/sources/${s.id}`);
                        setSources(prev => prev.filter(src => src.id !== s.id));
                        toast.success('Source deleted');
                      } catch (e) {
                        toast.error('Failed to delete source');
                      }
                    }
                  }}
                >
                  <FiTrash2 size={14} className="text-red-500" />
                </button>
              </div>
            </div>
            );
          })}
          
          {sources.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <FiDatabase size={48} className="mx-auto mb-2" />
                <p className="text-lg font-medium">No sources yet</p>
                <p className="text-sm">Click "New Data" to add content to this knowledge base</p>
                  </div>
                </div>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) {
            handleFiles(Array.from(e.target.files));
          }
        }}
      />

      {/* Upload controls removed per design */}

      {/* Action modals */}
      <TagModal />
      <WebScrapeModal />
    </div>
  );
};

export default KnowledgeBase;
